import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';

import { ImpersonationService, UserService } from '@pulpo/pulpo-api';
import { clearPossibleSubscriptions } from '@pulpo/pulpo-commons';
import { AGENCY_ADMIN_PERMISSION, FullUser, ImpersonationUrl, UserGroup } from '@pulpo/pulpo-models';

/**
 * Either the target is already known — the user list, where the row *is* the target — or only
 * the agency is, and its admin account has to be resolved first.
 */
export type ImpersonationDialogData =
  | { kind: 'user'; user: FullUser }
  | { kind: 'agency'; agency: UserGroup };

@Component({
  selector: 'app-impersonation-confirm-dialog',
  templateUrl: './impersonation-confirm-dialog.component.html',
  styleUrls: ['./impersonation-confirm-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
})
export class ImpersonationConfirmDialogComponent implements OnInit, OnDestroy {
  /** Candidates to pick from: the agency admins, or every active member when there is none. */
  candidates: FullUser[] = [];
  selected: FullUser | null = null;
  agencyName = '';
  /** True when no agency_admin was found and the list fell back to plain members. */
  fallbackToAnyMember = false;
  resolving = false;
  opening = false;
  errorMessage = '';

  private resolveSubscription: Subscription;
  private loginSubscription: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ImpersonationDialogData,
    public dialogRef: MatDialogRef<ImpersonationConfirmDialogComponent>,
    private impersonationService: ImpersonationService,
    private userService: UserService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.data.kind === 'user') {
      this.selected = this.data.user;
      this.candidates = [this.data.user];
      return;
    }
    this.agencyName = this.data.agency.name;
    this.resolveAgencyAdmin(this.data.agency);
  }

  ngOnDestroy(): void {
    clearPossibleSubscriptions(this.resolveSubscription, this.loginSubscription);
  }

  get targetName(): string {
    return this.selected ? `${this.selected.firstName} ${this.selected.lastName}` : '';
  }

  /**
   * The agency row carries user *counts*, never permissions, so the admin account can only be
   * resolved by loading the members — done here, on confirmation, rather than one request per
   * row on the list screen.
   */
  private resolveAgencyAdmin(agency: UserGroup): void {
    this.resolving = true;
    this.resolveSubscription = this.userService
      .queryFullUserPage({
        agencyGroupId: agency.id,
        page: 0,
        size: 1000,
        sort: 'lastName',
        direction: 'asc',
      })
      .subscribe({
        next: (res: HttpResponse<{ content: FullUser[] }>) => {
          const active = (res.body?.content ?? []).filter((user) => user.activated);
          const admins = active.filter((user) => this.isAgencyAdmin(user, agency));
          this.fallbackToAnyMember = admins.length === 0;
          this.candidates = admins.length > 0 ? admins : active;
          this.selected = this.candidates.length > 0 ? this.candidates[0] : null;
          if (this.candidates.length === 0) {
            this.errorMessage = "Cette agence n'a aucun utilisateur actif.";
          }
          this.resolving = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Impossible de charger les utilisateurs de cette agence.';
          this.resolving = false;
          this.cdr.markForCheck();
        },
      });
  }

  private isAgencyAdmin(user: FullUser, agency: UserGroup): boolean {
    const permissions = (user.groupDescriptions as any)?.[agency.name] ?? [];
    return permissions.includes(AGENCY_ADMIN_PERMISSION);
  }

  /**
   * The POST is asynchronous, so a `window.open` awaiting its response is no longer tied to the
   * click and gets blocked. The tab is therefore opened empty on the gesture itself, and only
   * pointed at the hand-off URL once it arrives.
   */
  confirm(): void {
    if (!this.selected || this.opening) {
      return;
    }
    this.opening = true;
    this.errorMessage = '';
    const tab = window.open('', '_blank');

    this.loginSubscription = this.impersonationService.loginAs(this.selected.id).subscribe({
      next: (res: HttpResponse<ImpersonationUrl>) => {
        const url = res.body?.url;
        if (!url) {
          tab?.close();
          this.fail("Le serveur n'a pas renvoyé d'URL de connexion.");
          return;
        }
        if (tab) {
          tab.location.href = url;
        } else {
          // Popup blocked despite the synchronous open: hand the URL over instead of losing it.
          window.location.href = url;
        }
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        tab?.close();
        this.fail(this.toMessage(err));
      },
    });
  }

  private fail(message: string): void {
    this.opening = false;
    this.errorMessage = message;
    this.cdr.markForCheck();
  }

  /**
   * Pulpo 400s carry `{ status, timestamp, error, debugMessage }`. The 403 comes from Spring
   * Security with no body at all, so it has to be handled on its status alone.
   */
  private toMessage(err: HttpErrorResponse): string {
    if (err.status === 403) {
      return "Vous n'avez pas le droit d'ouvrir une session au nom d'un utilisateur.";
    }
    if (err.status === 401) {
      return 'Votre session a expiré, reconnectez-vous.';
    }
    if (err.status === 400) {
      switch (err.error?.error) {
        case 'user.notFound':
          return "Cet utilisateur n'existe plus.";
        case 'impersonation.target.notAnAgencyMember':
          return "Cet utilisateur n'appartient à aucune agence.";
        case 'impersonation.user.invalidId':
          return "L'identifiant de cet utilisateur est invalide.";
        default:
          return 'Cet utilisateur ne peut pas être impersonnifié.';
      }
    }
    return "L'ouverture de session a échoué, réessayez.";
  }
}
